@use 'sass:map';
@use 'sass:math';
@use '../../scss/global' as *;
@use '../../scss/components' as *;
@use 'themes';

// ================================================================ //
// -- Local Variables

$dropdown-margin: 8px; // NOTE: should be same value as PopperJS offset
$submenu-padding-bottom-mobile: 1em;
$mega-menu-col-gap: 1.25em;
$mega-menu-padding-size: 2em;

$toggle-icon-width: map.get($icon-sizes, md);
$toggle-icon-line-weight: 3px;


// ================================================================ //
// -- Component Styles

:host {
  display: block;

  // FLUID Navigation: Base Styles
  @include navigation-base;


  // ============================================ //
  // -- Header Root

  .fluid-header {
    display: flex;
    justify-content: center;
    position: relative;
    flex-wrap: wrap;
    min-height: var(--header-min-height);
    background-color: var(--header-background-color);
    color: var(--header-color);
    padding-left: var(--G-content-padding-left);
    padding-right: var(--G-content-padding-right);
    border-bottom-width: 0;
    border-bottom-color: var(--header-border-color);
    border-bottom-style: solid;
    box-shadow: var(--G-header-box-shadow);

    @include transition(
      $properties: 'background-color',
      $duration: short
    );

    @include screen-lg {
      flex-wrap: nowrap;
      justify-content: flex-start;
      border-bottom-width: 1px;
    }
    @include screen-xl {
      flex-wrap: wrap;
    }

    // ==== Fixed Position

    &.header-fixed {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
    }

    // ==== Display Density

    &.display-density-compact {
      @include screen-lg {
        min-height: 60px;
      }
    }

    // ==== Disable Drop Shadow

    &.disable-shadow {
      box-shadow: none;
    }
  }


  // ============================================ //
  // -- "Flyout" Menu Toggle Button (Mobile)

  .header-mobile-toggle {
    @include button-reset;
    width: calc($toggle-icon-width + var(--G-content-padding-left) + var(--G-content-padding-right));
    height: 100%;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;

    @include screen-lg {
      display: none;
    }
  }

  // ==== Toggle Button Pseudo-Icon

  .toggle-icon-animation {
    width: $toggle-icon-width;
    height: $toggle-icon-line-weight;
    display: block;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background-color: currentColor;
    transition: background-color 10ms 300ms ease;

    &::before,
    &::after {
      width: $toggle-icon-width;
      height: $toggle-icon-line-weight;
      position: absolute;
      left: 0;
      right: 0;
      background-color: currentColor;
      content: '';
      transition: top 300ms 350ms ease,
      transform 300ms 50ms ease;
    }

    &::before {
      top: $toggle-icon-line-weight * -2.5;
    }
    &::after {
      top: $toggle-icon-line-weight * 2.5;
    }

    // == "Dismiss" State (i.e. "X")

    &.dismiss {
      background-color: transparent;

      &::before,
      &::after {
        top: 0;
        transition: top 300ms 50ms ease,
        transform 300ms 350ms ease;
      }

      &::before {
        transform: rotate(45deg);
      }
      &::after {
        transform: rotate(-45deg);
      }
    }
  }


  // ============================================ //
  // -- Logo

  .header-logo {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    min-height: var(--header-min-height);
    font-size: inherit; // Set for em units baseline

    @include screen-lg {
      min-height: auto;
      padding-left: 0;
      padding-right: 4em;
    }
  }

  // ==== Logo Icon

  .header-logo-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    @include screen-lg {
      padding-top: var(--logo-container-padding-top);
      padding-bottom: var(--logo-container-padding-bottom);
    }

    // == FLUID Logo / Custom Slotted Logo

    &.fluid-logo,
    &.slotted-logo {
      @include screen-lg {
        padding-top: 1em;
        padding-bottom: 1em;
      }
    }

    // == FLUID Logo

    &.fluid-logo {
      --logo-icon-height: 38px; // Overridden here since var doesn't change across themes

      @include screen-lg {
        --logo-icon-height: 42px;
      }
    }

    // == Embedded SVG

    svg {
      height: var(--logo-icon-height);
    }
  }


  // ============================================ //
  // -- Main Container / Mobile "Flyout" Menu

  .header-main {
    display: flex;
    flex-direction: column;
    width: 100vw;
    height: calc(100vh - var(--header-min-height)); // Overridden by inline styles
    position: fixed;
    top: var(--header-min-height); // Overridden by inline styles
    left: 0;
    transform: translateX(-110%); // 110% prevents shadow from being visible
    overflow-x: visible;
    overflow-y: auto;
    background-color: var(--mobile-menu-background-color);
    box-shadow: var(--G-header-box-shadow);
    clip-path: inset(0 -1em 0 0); // Clips box-shadow top
    will-change: transform;

    @include transition(
      $properties: 'transform',
      $duration: 400ms
    );

    @include screen-sm {
      max-width: var(--mobile-menu-width, 75vw);
    }
    @include screen-md {
      max-width: var(--mobile-menu-width, 60vw);
    }
    @include screen-lg {
      flex: 1 1 auto;
      flex-direction: row;
      flex-wrap: wrap;
      height: auto;
      min-width: 0;
      max-width: none;
      position: relative;
      top: auto;
      left: auto;
      overflow: visible;
      background-color: transparent;
      transform: none;
      box-shadow: none;
      clip-path: none;
      will-change: unset;

      @include transition($properties: none);
    }

    // == Open "Flyout" Menu

    &.header-mobile-open {
      transform: translateX(0);
    }

    // == Hidden "Flyout" Menu (Prevents FOUC)

    &.header-mobile-hidden {
      opacity: 0;
      visibility: hidden;
    }
  }


  // ============================================ //
  // -- Navigation Container

  .header-nav {
    flex: 0 1 auto;
    display: block;
    background-color: $white;

    @include screen-lg {
      flex: 1 1 auto;
      display: flex;
      background-color: transparent;

      &.align-left { justify-content: flex-start; }
      &.align-center { justify-content: center; }
      &.align-right { justify-content: flex-end; }
    }
  }


  // ============================================ //
  // -- Navigation List (<ul> element)

  .fluid-nav-list {
    display: block;

    @include screen-lg {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      height: 100%;
    }
  }


  // ============================================ //
  // -- Navigation Item (<li> element)

  .header-nav-item {
    width: 100%;
    display: block;

    @include screen-lg {
      width: auto;
      display: flex;
    }

    // == Bottom Border (mobile only)

    &:not(:last-of-type) {
      border-width: 0 0 1px 0;
      border-style: solid;
      border-color: var(--accent-border-color);

      @include screen-lg {
        border-width: 0;
      }
    }

    // == Contains Submenu / Mega Menu Child

    &.has-submenu,
    &.has-mega-menu {
      position: relative;

      // At mobile size, remove borders between nav items within submenus or mega menus
      .header-nav-item {
        border-width: 0;
      }
    }
  }


  // ============================================ //
  // -- Submenu / Mega Menu Container (Shared)

  .header-submenu,
  .header-mega-menu {
    background-color: $white;
    outline: 0;

    @include screen-lg {
      position: absolute;
      border-width: 1px;
      border-style: solid;
      border-color: var(--accent-border-color);
      border-radius: var(--G-border-radius);
      box-shadow: var(--submenu-box-shadow);

      @include screen-ie-only {
        top: 100%;
      }
    }

    // == Pseudo-Border (retains :hover state)

    &::before {
      @include screen-lg {
        display: block;
        position: absolute;
        top: -$dropdown-margin - 1px;
        bottom: -1em;
        left: -1em;
        right: -1em;
        content: '';
        z-index: -1;
      }
    }

    // == Open State

    &.submenu-open,
    &.mega-menu-open {
      @include screen-lg {
        @include fade-in();
      }
    }

    // == Closed State

    &.submenu-closed,
    &.mega-menu-closed {
      @include screen-lg-down {
        display: none;
      }
      @include screen-lg {
        @include fade-out();
      }
    }

    // == Submenu Indentation (mobile only)

    .submenu-link,
    .mega-menu-column-title {
      @include screen-lg-down {
        padding-left: calc(var(--submenu-item-padding-left) * 2);
      }
    }
  }


  // ============================================ //
  // -- Submenu Container

  .header-submenu {
    padding-bottom: $submenu-padding-bottom-mobile;

    @include screen-lg {
      width: max-content;
      min-width: 115%;
      white-space: nowrap;
      padding-top: 0.5em;
      padding-bottom: 0.5em;
      z-index: 1;
    }
  }


  // ============================================ //
  // -- Mega Menu: Parent Container

  .header-mega-menu {

    @include screen-lg {
      width: max-content;
      min-width: min(75vw, 800px);
      max-width: min(98vw, 1400px);
      display: flex;
      z-index: 1;
    }
    @include screen-xl {
      min-width: min(60vw, 1200px);
    }

    // == Desktop Link Hover State (rounded edges)

    .submenu-link {
      @include screen-lg {
        border-radius: 0.4em;
      }
    }
  }


  // ============================================ //
  // -- Mega Menu: Menu Title / Slot (left)

  .mega-menu-panel {
    color: var(--G-color-text);

    @include screen-lg {
      flex: 1 1 20%;
      min-width: 250px;
      background-color: var(--mega-menu-panel-background-color);
      padding-top: $mega-menu-padding-size;
      padding-bottom: $mega-menu-padding-size;
      padding-left: $mega-menu-padding-size;
      padding-right: $mega-menu-padding-size;
    }

    // == Panel + Content Container

    & + .mega-menu-content {
      justify-content: flex-start;
    }
  }

  // ==== Menu Title

  .mega-menu-title {
    display: none; // Hide at mobile size
    font-size: inherit;

    @include screen-lg {
      display: block;
      font-size: 1.5em;
      font-weight: $font-weight-light;
    }
  }


  // ============================================ //
  // -- Mega Menu: Content Container (right)

  .mega-menu-content {

    @include screen-lg {
      flex: 1 1 auto;
      display: flex;
      justify-content: center;
      column-gap: $mega-menu-col-gap;
      padding-top: $mega-menu-padding-size;
      padding-bottom: $mega-menu-padding-size;
      padding-left: $mega-menu-padding-size;
      padding-right: $mega-menu-padding-size;
    }
  }


  // ============================================ //
  // -- Mega Menu: Column

  .mega-menu-column {

    @include screen-lg {
      min-width: var(--mega-menu-column-min-width);

      &.mega-menu-col-2 { max-width: 50%; }
      &.mega-menu-col-3 { max-width: 33%; }
      &.mega-menu-col-4 { max-width: 25%; }
      &.mega-menu-col-5 { max-width: 20%; }
      &.mega-menu-col-6 { max-width: 16.5%; }
    }
    @include screen-ie-only {
      // Fallbacks for "column-gap" property
      padding-left: math.div($mega-menu-col-gap, 2);
      padding-right: math.div($mega-menu-col-gap, 2);
    }

    // == "Hybrid" Layout ("unstructured" links after columns)

    & + .mega-menu-submenu.grid-layout {
      position: relative;
      padding-top: $mega-menu-col-gap;

      @include screen-lg {
        padding-left: $mega-menu-col-gap;
      }

      // Divider line between last column & "unstructured" links

      &::before {
        height: var(--G-border-width);
        display: block;
        position: absolute;
        top: 4px;
        left: 2.5em;
        right: 2.5em;
        background-color: var(--accent-border-color);
        content: "";

        @include screen-lg {
          width: var(--G-border-width);
          height: auto;
          top: 1em;
          bottom: 1em;
          left: 0;
        }
      }
    }
  }

  // ==== Column Title

  .mega-menu-column-title {
    font-size: 1em;
    color: var(--mega-menu-column-title-color);
    background-color: $white;
    padding-top: 0.5em;
    padding-bottom: 0.5em;

    @include screen-lg {
      padding-top: 0;
      padding-left: var(--submenu-item-padding-left);
      padding-right: var(--submenu-item-padding-right);
    }

    .column-title-text {
      font-size: var(--mega-menu-column-title-font-size);
      text-transform: uppercase;
      text-decoration: underline;
      color: inherit;
    }
  }


  // ============================================ //
  // -- Mega Menu: Submenu List (<ul> element)

  .mega-menu-submenu {
    @include list-reset;
    background-color: $white;
    padding-bottom: $submenu-padding-bottom-mobile;

    @include screen-lg {
      padding-bottom: 0;
    }

    // == Grid Layout for "Unstructured" Links

    &.grid-layout {

      @include screen-lg {
        height: 100%;
        display: grid;
        grid-auto-flow: column;
        grid-template-rows: repeat(8, auto);
        align-content: flex-start;
        column-gap: $mega-menu-col-gap;
      }

      .header-nav-item {
        @include screen-lg {
          white-space: nowrap;
        }
      }
    }

    .header-nav-item {
      display: block;
    }

    .header-link {
      width: 100%;
    }
  }


  // ============================================ //
  // -- Link / Submenu Buttons

  .header-link,
  .submenu-link,
  .header-submenu-button,
  .header-mega-menu-button {
    width: 100%;
    display: flex;
    align-items: center;
    font-family: inherit;
    font-size: var(--link-font-size);
    font-weight: var(--link-font-weight);
    line-height: inherit;
    color: var(--link-color);
    text-align: left;
    text-decoration: none;
    background-color: $white;
    border: 0;

    @include transition(
      $properties: 'background-color, color, font-weight',
      $duration: rapid,
    );

    @include screen-lg {
      outline: 0;
      background-color: transparent;
    }

    // == Selected State / Submenu Contains Selected Item

    &.button-selected,
    &.selected-item-parent {
      background-color: var(--link-selected-background-color);
      color: var(--link-selected-color);
      font-weight: $font-weight-bold;
      text-decoration: underline;

      @include screen-lg {
        text-decoration: var(--link-selected-text-decoration);
      }

      .fluid-nav-submenu-icon {
        color: var(--link-color); // Sets FLUID Icon "fill" color

        @include screen-lg {
          color: inherit; // Sets FLUID Icon "fill" color
        }
      }
    }

    // == Hover, Focus, & Submenu Open States

    &:hover,
    &:focus-visible,
    &.submenu-active {

      @include screen-lg {
        background-color: var(--link-hover-background-color);
        color: var(--link-hover-color);
        text-decoration: var(--link-hover-text-decoration);
      }

      &.button-selected {
        @include screen-lg {
          color: var(--link-hover-color);
          text-decoration: none;
        }
      }
    }

    // == Disabled State

    &.button-disabled {
      cursor: not-allowed;
      text-decoration: none !important;
      font-weight: $font-weight-regular !important;
      outline: 0;

      @include screen-lg {
        background-color: transparent !important;
      }

      .fluid-nav-label,
      .fluid-nav-submenu-icon {
        color: var(--link-disabled-color);
        opacity: var(--link-disabled-opacity);
      }
    }

    // == Dropdown Icon

    .fluid-nav-submenu-icon {
      color: inherit; // Sets FLUID Icon "fill" color
      margin-left: auto;

      @include screen-lg {
        margin-left: 0.75em;
      }
    }
  }

  // ==== "Top-Level" Link / Submenu Buttons

  .header-link,
  .header-submenu-button,
  .header-mega-menu-button {
    padding-top: var(--item-padding-top);
    padding-bottom: var(--item-padding-bottom);
    padding-left: var(--item-padding-left);
    padding-right: var(--item-padding-right);

    @include screen-lg {
      width: auto;
      min-height: var(--section-min-height);
      line-height: 1.4;
      text-align: center;
    }
  }

  // ==== Submenu Link

  .submenu-link {
    padding-top: var(--submenu-item-padding-top);
    padding-bottom: var(--submenu-item-padding-bottom);
    padding-left: var(--submenu-item-padding-left);
    padding-right: var(--submenu-item-padding-right);

    @include screen-lg {
      flex: 1 1 100%;
      max-width: 100%;
    }
  }


  // ============================================ //
  // -- Search (Slot)

  .header-search-slot {
    flex: 0 0 auto;
    order: -1; // Move to top of flyout menu
    display: flex;
    align-items: center;
    padding-top: var(--section-padding-top);
    padding-bottom: var(--section-padding-bottom);
    padding-left: var(--section-padding-left);
    padding-right: var(--section-padding-right);

    @include screen-lg {
      min-height: var(--section-min-height);
      flex: 0 1 auto;
      order: 0;
      justify-content: flex-end;
      background-color: transparent;
      padding: 0;
      margin-right: var(--G-content-padding-right);
      margin-left: auto;
    }

    & + .header-content-slot {
      @include screen-lg {
        margin-left: 0;
      }
    }
  }


  // ============================================ //
  // -- Content (Slot)

  .header-content-slot {
    flex: 0 0 auto;
    display: flex;
    flex-wrap: wrap;
    padding-top: var(--section-padding-top);
    padding-bottom: var(--section-padding-bottom);
    padding-left: var(--section-padding-left);
    padding-right: var(--section-padding-right);

    @include screen-lg {
      min-height: var(--section-min-height);
      flex: 0 1 auto;
      align-items: center;
      padding: 0;
      margin-left: auto;
    }
  }


  // ============================================ //
  // -- "Sticky" Header Placeholder

  .header-placeholder {
    display: block;
    width: 100%;
  }
}
